import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RiSparklingLine, RiSendPlane2Line, RiLoader2Line, RiCloseLine } from '@remixicon/react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';

interface SchemaAIAssistantProps {
  databaseId: string;
}

export function SchemaAIAssistant({ databaseId }: SchemaAIAssistantProps) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<string>('');

  const exampleQuestions = [
    "What tables are in this database?",
    "Show me the relationships",
    "Which tables have foreign keys?",
    "Explain the schema structure",
  ];

  const handleAsk = async (q?: string) => {
    const queryText = q || question;
    if (!queryText.trim()) {
      toast.error('Please enter a question');
      return;
    }

    setLoading(true);
    setResponse('');

    try {
      const result = await api.answerSchemaQuestion(databaseId, queryText);
      setResponse(result.answer);
      setQuestion('');
    } catch (error: any) {
      toast.error('Failed to get AI response');
      setResponse('Unable to answer at the moment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example: string) => {
    setQuestion(example);
    handleAsk(example);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="gap-2 bg-card shadow-lg border-primary/20 hover:border-primary/40 hover:bg-accent"
        >
          <RiSparklingLine className="h-4 w-4 text-primary" />
          <span className="text-xs">Ask AI</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        side="left"
        sideOffset={10}
      >
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10">
              <RiSparklingLine className="h-3.5 w-3.5 text-primary" />
            </div>
            <h3 className="text-sm font-semibold">Schema Assistant</h3>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setOpen(false)}
          >
            <RiCloseLine className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-3 space-y-3 max-h-[400px] overflow-y-auto">
          {!response && !loading && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Try asking:</p>
              <div className="flex flex-wrap gap-1.5">
                {exampleQuestions.map((example) => (
                  <button
                    key={example}
                    onClick={() => handleExampleClick(example)}
                    className="text-xs px-2 py-1 rounded-md bg-muted hover:bg-accent transition-colors text-left"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}

          {response && (
            <Card className="p-3 bg-muted/50 border-primary/10">
              <p className="text-sm leading-relaxed">{response}</p>
            </Card>
          )}

          {loading && (
            <div className="flex items-center gap-2 py-4">
              <RiLoader2Line className="h-4 w-4 animate-spin text-primary" />
              <p className="text-xs text-muted-foreground">Thinking...</p>
            </div>
          )}
        </div>

        <div className="p-3 border-t">
          <div className="flex gap-2">
            <Input
              placeholder="Ask about the schema..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !loading) {
                  handleAsk();
                }
              }}
              disabled={loading}
              className="text-sm h-8"
            />
            <Button
              size="icon"
              onClick={() => handleAsk()}
              disabled={loading || !question.trim()}
              className="h-8 w-8 shrink-0"
            >
              {loading ? (
                <RiLoader2Line className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RiSendPlane2Line className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Powered by AI
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
